import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Flex, Form, Modal, Space, Typography } from 'antd';
import Table from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import { type TFunction } from 'i18next';
import React, { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hrConfirmBusinessTripTicket } from 'src/apis/hr_ticket/businessTripTicket.api';
import { InputNumber } from 'src/components/v2/InputNumber';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import useCalcTimeBusiness from 'src/hooks/useCalcTimeBusiness';
import {
  type BusinessTripTicketConfirm,
  type IBusinessTripTicketDetail
} from 'src/types/hr_ticket/businessTripTicket.type';
import { showNotification } from 'src/utils/notification';
import { formatDate, formatTimeString, totalOfRangeDate } from 'src/utils/utils';
import { type OpenConfirmType } from '../BusinessTripTicketDetail/BusinessTripTicketDetail';
const { Text } = Typography;

interface Props {
  open: OpenConfirmType;
  setOpen: React.Dispatch<React.SetStateAction<OpenConfirmType>>;
}

const columns = function (
  formState: FormStateType,
  editingKey: string,
  setFormState: Dispatch<SetStateAction<FormStateType>>,
  callbackSave: () => void,
  callbackEdit: (record: Partial<IBusinessTripTicketDetail>) => void,
  callbackCancel: (record: Partial<IBusinessTripTicketDetail>) => void,
  calcTotalTime: (
    startTimeInput: string | undefined,
    endTimeInput: string | undefined,
    employeeId: number | undefined
  ) => number,
  t: TFunction<'translation', undefined, 'translation'>
): ColumnsType<Partial<IBusinessTripTicketDetail>> {
  const columnTitle: ColumnsType<Partial<IBusinessTripTicketDetail>> = [
    {
      title: 'Người đăng ký',
      dataIndex: 'employee',
      key: 'employee',
      render: (_, record) => (
        <Text>
          {record.employee?.code} - {record.employee?.fullName}
        </Text>
      )
    },
    {
      title: 'Ngày công tác',
      dataIndex: 'date',
      key: 'date',
      align: 'center',
      render: (_, record) => {
        return (
          <Flex vertical gap={5}>
            <Text>
              {formatDate(record?.fromDate)} - {formatDate(record?.toDate)}
            </Text>
            <Text strong>Tổng: {totalOfRangeDate(record?.fromDate, record?.toDate)} Ngày</Text>
          </Flex>
        );
      }
    },
    {
      title: 'Thời gian',
      dataIndex: '',
      key: 'time',
      align: 'center',
      render: (_, record) => (
        <Flex vertical gap={5}>
          <Text>
            {formatTimeString(record?.startTime, true)} - {formatTimeString(record?.endTime)}
          </Text>
          <Text strong>Tổng: {calcTotalTime(record?.startTime, record?.endTime, record.employeeId)} Tiếng</Text>
        </Flex>
      )
    },
    {
      title: <Text>{t('Số ngày lưu trú')}</Text>,
      dataIndex: 'numDaysOfStay',
      align: 'center',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='numDaysOfStay' style={{ marginBottom: 0 }}>
              <InputNumber onChange={(value) => setFormState((prev) => ({ ...prev, numDaysOfStay: value }))} />
            </Form.Item>
          );
        }
        return <Text>{record.numDaysOfStay} Ngày</Text>;
      }
    },
    {
      title: <Text>{t('Số ngày công tác phí')}</Text>,
      dataIndex: 'numDaysOfBusinessTrip',
      align: 'center',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='numDaysOfBusinessTrip' style={{ marginBottom: 0 }}>
              <InputNumber onChange={(value) => setFormState((prev) => ({ ...prev, numDaysOfBusinessTrip: value }))} />
            </Form.Item>
          );
        }
        return <Text>{record.numDaysOfBusinessTrip} Ngày</Text>;
      }
    },
    {
      title: 'Xác nhận',
      dataIndex: '',
      key: 'time',
      align: 'center',
      render: (_, record) => <Checkbox checked disabled />
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center',
      width: '8%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Space size='small'>
              <Button type='link' size='small' onClick={callbackSave}>
                {t('action.save')}
              </Button>
              <Button type='link' size='small' danger onClick={() => callbackCancel(record)}>
                {t('action.cancel')}
              </Button>
            </Space>
          );
        }
        return (
          <Button type='link' disabled={editingKey !== ''} size='small' onClick={() => callbackEdit(record)}>
            {t('action.edit')}
          </Button>
        );
      }
    }
  ];

  return columnTitle;
};

type FormStateType = {
  id?: string | number;
  numDaysOfStay?: number;
  numDaysOfBusinessTrip?: number;
};

const initialFormState: FormStateType = {
  id: undefined,
  numDaysOfStay: 0,
  numDaysOfBusinessTrip: 0
};

function HrConfirmBusinessTripTicket({ open, setOpen }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState('');
  const [formState, setFormState] = useState(initialFormState);
  const { calcTotalTime } = useCalcTimeBusiness();
  const [businessTripTicketDetails, setBusinessTripTicketDetails] = useState<IBusinessTripTicketDetail[]>([]);

  useEffect(() => {
    if (open.confirms) {
      setBusinessTripTicketDetails(open.confirms);
    }
  }, [open.confirms]);

  const hrConfirmBusinessTripMutation = useMutation({
    mutationFn: (businessTripTicketDetail: BusinessTripTicketConfirm) =>
      hrConfirmBusinessTripTicket(Number(open.id), businessTripTicketDetail)
  });

  const handleSubmit = () => {
    hrConfirmBusinessTripMutation.mutate(
      {
        confirmNote: '',
        confirmType: ConfirmTypeEn.Confirmed,
        confirms: businessTripTicketDetails
      },
      {
        onSuccess: () => {
          setOpen({ isOpen: false });
          queryClient.invalidateQueries({ queryKey: ['businessTripTicket', String(open.id)] });
          showNotification('Xác nhận phiếu thành công', 'success');
        }
      }
    );
  };

  const handleClose = () => {
    setOpen({ isOpen: false });
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(() => {
        let cloneDataSource = [...businessTripTicketDetails];
        const index = cloneDataSource?.findIndex((item) => item.id === formState.id);
        if (index === -1) return;

        cloneDataSource[index].numDaysOfStay = formState.numDaysOfStay || 0;
        cloneDataSource[index].numDaysOfBusinessTrip = formState.numDaysOfBusinessTrip || 0;

        setBusinessTripTicketDetails(cloneDataSource);
        setFormState(initialFormState);
        setEditingKey('');
      })
      .catch(() => {});
  };

  const handleEdit = (record: Partial<IBusinessTripTicketDetail>) => {
    setFormState({
      id: record.id,
      numDaysOfStay: record.numDaysOfStay,
      numDaysOfBusinessTrip: record.numDaysOfBusinessTrip
    });
    setEditingKey(String(record.id));
  };

  const handleCancel = () => {
    setFormState(initialFormState);
    setEditingKey('');
  };

  return (
    <Modal
      title={
        <Flex gap={4}>
          <Text>Xác nhận công tác</Text>
        </Flex>
      }
      width={1200}
      centered
      maskClosable={false}
      open={open.isOpen}
      onOk={handleSubmit}
      onCancel={handleClose}
      footer={[
        <Button key='back' onClick={handleClose}>
          {t('Hủy')}
        </Button>,
        <Button key='submit' type='primary' onClick={handleSubmit} loading={hrConfirmBusinessTripMutation.isLoading}>
          {t('Xác nhận')}
        </Button>
      ]}
    >
      <Form
        form={form}
        fields={[
          {
            name: 'numDaysOfStay',
            value: formState.numDaysOfStay
          },
          {
            name: 'numDaysOfBusinessTrip',
            value: formState.numDaysOfBusinessTrip
          }
        ]}
      >
        <Table
          className='business-trip-confirm-table'
          columns={columns(formState, editingKey, setFormState, handleSave, handleEdit, handleCancel, calcTotalTime, t)}
          rowKey={(record) => String(record.id)}
          dataSource={open.confirms}
          pagination={false}
          bordered={false}
          size='middle'
        />
      </Form>
    </Modal>
  );
}

export default HrConfirmBusinessTripTicket;
